import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { surveyFormSchema, type SurveyFormData } from '@/utils/validation';
import { createSurveyResponse } from '@/services/survey.service';
import { useState } from 'react';

export const useSurveyForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const form = useForm<SurveyFormData>({
    resolver: zodResolver(surveyFormSchema),
    defaultValues: {
      details: {
        customerName: '',
        companyName: '',
        productOrService: '',
        engagementDate: '',
        contactPerson: '',
      },
      ratings: {
        productQuality_expectationsMet: 3,
        productQuality_performanceAsExpected: 3,
        delivery_timeliness: 3,
        delivery_processEfficient: 3,
        communication_clearTimely: 3,
        communication_promptResponses: 3,
        staff_professionalCourteousHelpful: 3,
        staff_supportAdequateEffective: 3,
        price_valueForMoney: 3,
        overall_overallSatisfied: 3,
        overall_wouldRecommend: 3,
      },
      open: {
        likesMost: '',
        areasForImprovement: '',
        wouldDoBusinessAgain: '',
      },
      consent: {
        agreed: false,
        timestamp: '',
      },
    },
    mode: 'onBlur',
  });

  const onSubmit = async (data: SurveyFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const consentWithTimestamp = {
        ...data.consent,
        timestamp: new Date().toISOString(),
      };

      await createSurveyResponse(
        data.details,
        data.ratings,
        data.open,
        consentWithTimestamp
      );

      setSubmitSuccess(true);
      form.reset();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to submit survey';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    submitError,
    submitSuccess,
  };
};

